import PropTypes from 'prop-types';
import { Component } from 'react';
import { Dropdown, MenuItem } from 'react-bootstrap';
import _ from 'underscore';
import { compactRevision } from '../../helpers/revisions';
import { get } from '../../redux/api';
import type { Revision } from '../../types';
import utils from '../../utils';
import Loader from '../generic/Loader';
import { CommitPill } from '../generic/Pill';
import SearchInput from '../generic/SearchInput';
import Tooltip from '../generic/Tooltip';
import { getRevisionMessage } from './DisplayProjectNav/RevisionDropdown/utils';

const MAX_REV_COUNT = 12;

// var TODO: add revision autocomplete

type RevisionDropdownProps = {
  onSelectRevision?: Function;
  selectedRevision?: Revision;
  reference?: string;
  project?: any;
  referenceType?: string;
  resolvedRevisions?: any;
  expanded?: boolean;
  maxRevisions?: number;
  onlyCommit?: boolean;
};

type RevisionDropdownState = {
  locatorFilter?: string;
  dropdownOpen?: boolean;
  loading_revisions?: boolean;
  revisions?: Revision[];
};

class RevisionDropdown extends Component<RevisionDropdownProps, RevisionDropdownState> {
  static propTypes = {
    onSelectRevision: PropTypes.func,
    selectRevision: PropTypes.object,
    project: PropTypes.object,
    reference_type: PropTypes.string,
    resolvedRevisions: PropTypes.bool,
    reference: PropTypes.string,
    expanded: PropTypes.bool,
    maxRevisions: PropTypes.number,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      locatorFilter: '',
      dropdownOpen: props.expanded,
      loading_revisions: false,
      revisions: [],
    };
    this.onSearchChange = _.debounce(() => {
      this.loadRevisions(props.reference);
    }, 250);
  }

  onSearchChange;

  componentDidMount() {
    if (this.props.expanded) {
      this.loadRevisions(this.props.reference);
    }
    window.addEventListener('click', this.handleClick);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClick);
  }

  UNSAFE_componentWillReceiveProps(nextProps : RevisionDropdownProps) {
    if (this.props.expanded && nextProps.referenceType && nextProps.reference) {
      const currentRefName = this.props.reference;
      const nextRefName = nextProps.reference;
      if (nextRefName && currentRefName !== nextRefName) {
        this.loadRevisions(nextRefName, nextProps.referenceType);
      }
    }
  }

  //  For provided builds, the revisions are going to be a timestamp, which can confuse users
  //  as it sort of implies that it is a commit timestamp and not the time we analysed the deps.
  get shouldShowAnalyzedLabel(): boolean {
    return this.props.project?.data?.require_mediated_dependencies ?? false;
  }

  handleLocatorFilterChange = ev => {
    this.setState({ locatorFilter: ev.target.value });
    this.onSearchChange();
  };

  getRevisionMessage = (revision: Revision, isSelected?: boolean) => {
    return getRevisionMessage({
      revision,
      isSelected: isSelected || false,
      shouldShowAnalyzedLabel: this.shouldShowAnalyzedLabel,
    });
  };

  handleClick = () => {
    if (this.state.dropdownOpen) {
      this.setState({ dropdownOpen: false });
    }
  };

  selectRevision = (revision, branch, ev) => {
    if (ev) {
      this.handleClick(ev);
    }
    if (this.props.onSelectRevision) {
      return this.props.onSelectRevision(revision, branch);
    }
    return this.context.router.push(
      `/projects/${
        utils.nuc(this.props.project.data.locator)
      }/refs/${
        utils.nuc(this.props.referenceType)
      }/${
        utils.nuc(this.props.reference)
      }/${
        utils.nuc(revision.loc.revision)}`,
    );
  };

  loadRevisions = (refName, type) => {
    if (!this.props.project.data.locator) {
      return;
    }
    this.setState({
      loading_revisions: true,
    });
    get(
      `/api/projects/${utils.nuc(this.props.project.data.locator)}/revisions`,
      false,
      {
        locator: this.state.locatorFilter,
        count: MAX_REV_COUNT + 1,
        offset: 0,
        refs: [refName],
        refs_type: type || this.props.referenceType,
        resolved: this.props.resolvedRevisions || '',
      },
    )
      .then(resp => {
        const revisions = resp.data;
        let revs = this.state.revisions;
        revs = revisions[this.props.referenceType][refName];
        this.setState({
          revisions: revs,
          loading_revisions: false,
        });
        return null;
      })
      .catch(_err => {
        this.setState({
          revisions: [],
          loading_revisions: false,
        });
      });
  };

  renderMenuItems = () => {
    const items = [
      <div key="search-input" className="margin-sm" style={{ minWidth: '200px' }}>
        <SearchInput
          type="text"
          placeholder="Search history by commit id..."
          value={this.state.locatorFilter}
          onChange={this.handleLocatorFilterChange}
        />
      </div>,
    ];

    if (this.state.revisions && !this.state.loading_revisions) {
      _.each(_.first(this.state.revisions, this.props.maxRevisions || MAX_REV_COUNT) || [], rev => {
        const isActive = this.props.selectedRevision && rev.locator === this.props.selectedRevision.locator;
        const revisionMessage = this.getRevisionMessage(rev);
        items.push(
          <Tooltip
            tooltip={this.getRevisionMessage(rev, true)}
            placement="right"
            id={`revision-dropdown-tooltip-${rev.locator}`}>
            <MenuItem
              key={`revision-dropdown-item-${rev.locator}`}
              className={isActive ? 'active' : ''}
              onSelect={this.selectRevision.bind(this, rev)}>
              <CommitPill revision={rev} label={compactRevision(rev.locator)} />
              {' '}
              {revisionMessage || rev.loc.revision}
            </MenuItem>
          </Tooltip>,
        );
      });
      if (this.state.revisions.length > (this.props.maxRevisions || MAX_REV_COUNT)) {
        items.push(
          <MenuItem disabled className="text-center">
            <i>More hidden...</i>
          </MenuItem>,
        );
      }
    } else {
      items.push(<Loader />);
    }
    return items;
  };

  render() {
    if (!this.props.selectedRevision) {
      return <a>...</a>;
    }
    if (this.props.expanded) {
      return (
        <ul className="nav nav-pills nav-stacked">
          {this.renderMenuItems()}
        </ul>
      );
    }
    const label = compactRevision(this.props.selectedRevision.locator);
    const selectedRevisionMessage = this.getRevisionMessage(this.props.selectedRevision, true) || '...';
    return (
      <Dropdown id="revisionDropdownButton" className="project-revision-dropdown" open={this.state.dropdownOpen}>
        <Dropdown.Toggle
          bsRole="toggle"
          onClick={utils.containEvent(() => {
            if (!this.state.dropdownOpen) {
              this.loadRevisions(this.props.reference);
            }
            this.setState({ dropdownOpen: !this.state.dropdownOpen });
          })}>
          {
            this.props.onlyCommit
              ? (
                <a>
                  <span>{label}</span>
                </a>
                )
              : (
                <span>
                  <code>{label}</code>
                  {!this.shouldShowAnalyzedLabel ? ': ' : ' '}
                  <span className="underline">
                    {utils.trimString(selectedRevisionMessage, 150) || '...'}
                  </span>
                </span>
                )
          }
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="dropdown-menu"
          onClick={utils.stopEventPropagation}>
          {this.renderMenuItems()}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}

export default RevisionDropdown;
